import { useCallback, useMemo, useRef, useState } from 'react';
import { useApprovalSelection } from './hooks/useApprovalSelection';
import { Check, ChevronRight, ChevronsUpDown, Layers, Search, X } from 'lucide-react';
import MainContainer from '@/components/containers/MainContainer';
import { Badge } from '@/components/ui/badge';
import { SpinnerOverlay } from '@/components/ui/Spinner';
import { Button } from '@/components/ui/Button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/Dropdown';
import { cn } from '@/utils/cn';
import { OBJECT_TYPE_LABELS, REQUEST_SOURCE_LABELS } from './constants';
import type { ApprovalRequest, ApprovalRequestSource, ApprovalStatus, ObjectType } from './types';
import {
  canSeeApprovalQueue,
  useApprovalContext,
  useApprovalDetail,
  useApprovalSummary,
  useApprovalsInfinite,
} from './hooks/useApprovals';
import ApprovalDetail from './components/ApprovalDetail';
import { dateBucket, relativeTime, shortId, STATUS_VISUAL } from './helpers';

type StatusFilter = ApprovalStatus | 'closed' | 'all';

type GroupMode = 'date' | 'type' | 'who' | 'none';
type SortMode = 'new' | 'old';

const STATUS_TABS: { value: StatusFilter; label: string }[] = [
  { value: 'pending', label: 'Čekající' },
  { value: 'all', label: 'Vše' },
  { value: 'draft', label: 'Rozpracováno' },
  { value: 'closed', label: 'Uzavřené' },
];

const OBJECT_TYPE_OPTIONS = Object.entries(OBJECT_TYPE_LABELS) as [ObjectType, string][];
const SOURCE_OPTIONS = Object.entries(REQUEST_SOURCE_LABELS) as [ApprovalRequestSource, string][];

const RANGE_OPTIONS = [
  { v: 'all', l: 'Vše' },
  { v: '7', l: '7 dní' },
  { v: '30', l: '30 dní' },
  { v: 'q', l: 'Čtvrtletí' },
] as const;
type RangeMode = typeof RANGE_OPTIONS[number]['v'];

const GROUP_LABELS: Record<GroupMode, string> = {
  date: 'Podle data',
  type: 'Podle typu',
  who: 'Podle žadatele',
  none: 'Bez seskupení',
};

const SORT_LABELS: Record<SortMode, string> = {
  new: 'Nejnovější',
  old: 'Nejstarší',
};

const withinRange = (value: string | null | undefined, range: RangeMode): boolean => {
  if (range === 'all') return true;
  if (!value) return false;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return false;
  const now = Date.now();
  const dayMs = 86400000;
  if (range === '7') return now - d.getTime() <= 7 * dayMs;
  if (range === '30') return now - d.getTime() <= 30 * dayMs;
  // aktuální čtvrtletí
  const nd = new Date();
  const qStart = new Date(nd.getFullYear(), Math.floor(nd.getMonth() / 3) * 3, 1).getTime();
  return d.getTime() >= qStart;
};

// ---------- levý panel — malé prvky ----------

const FacetRow = ({
  label,
  checked,
  onToggle,
}: {
  label: string;
  checked: boolean;
  onToggle: () => void;
}) => (
  <button
    type="button"
    onClick={onToggle}
    className={cn(
      'w-full flex items-center gap-2 pl-2 pr-2 py-[5px] rounded text-left transition-colors group',
      checked ? 'bg-primary-50' : 'hover:bg-neutral-50'
    )}
  >
    <span
      className={cn(
        'h-3.5 w-3.5 rounded-[3px] border shrink-0 inline-flex items-center justify-center transition-colors',
        checked
          ? 'bg-primary-600 border-primary-600'
          : 'border-neutral-300 bg-white group-hover:border-neutral-400'
      )}
    >
      {checked && <Check size={10} strokeWidth={3} className="text-white" />}
    </span>
    <span
      className={cn(
        'flex-1 truncate text-[13px]',
        checked ? 'text-primary-700 font-medium' : 'text-neutral-700'
      )}
    >
      {label}
    </span>
  </button>
);

const FacetGroup = ({
  title,
  options,
  selected,
  onChange,
  defaultOpen = true,
}: {
  title: string;
  options: [string, string][];
  selected: string[];
  onChange: (next: string[]) => void;
  defaultOpen?: boolean;
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const nSel = options.filter(([id]) => selected.includes(id)).length;
  return (
    <div className="border-t border-neutral-100 first:border-t-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-1.5 py-2 text-left group"
      >
        <ChevronRight
          size={11}
          className={cn('text-neutral-400 transition-transform', open && 'rotate-90')}
        />
        <span className="text-[11px] font-semibold uppercase tracking-wider text-neutral-500 group-hover:text-neutral-700">
          {title}
        </span>
        {nSel > 0 && (
          <Badge variant="brand" size="sm">
            {nSel}
          </Badge>
        )}
        {nSel > 0 && (
          <span
            role="button"
            onClick={(e) => {
              e.stopPropagation();
              onChange([]);
            }}
            className="ml-auto text-[11px] text-neutral-400 hover:text-primary-600"
          >
            Zrušit
          </span>
        )}
      </button>
      {open && (
        <div className="pb-2 space-y-px">
          {options.map(([id, label]) => (
            <FacetRow
              key={id}
              label={label}
              checked={selected.includes(id)}
              onToggle={() =>
                onChange(
                  selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]
                )
              }
            />
          ))}
        </div>
      )}
    </div>
  );
};

const FilterChip = ({ label, onRemove }: { label: string; onRemove: () => void }) => (
  <span className="inline-flex items-center gap-1 h-6 pl-2 pr-1 rounded bg-primary-50 border border-primary-100 text-[11px] font-medium text-primary-700">
    {label}
    <button
      type="button"
      onClick={onRemove}
      className="h-4 w-4 inline-flex items-center justify-center rounded hover:bg-primary-100 text-primary-600"
    >
      <X size={10} strokeWidth={2.5} />
    </button>
  </span>
);

const RequestRow = ({
  item,
  active,
  requesterName,
  onClick,
}: {
  item: ApprovalRequest;
  active: boolean;
  requesterName?: string;
  onClick: () => void;
}) => {
  const visual = STATUS_VISUAL[item.status];
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid="approval-list-item"
      className={cn(
        'w-full text-left flex gap-2.5 border-l-2 transition-colors px-3 py-2.5',
        active
          ? 'bg-primary-50/70 border-l-primary-600'
          : 'border-l-transparent hover:bg-neutral-25'
      )}
    >
      <span className={cn('mt-[7px] h-1.5 w-1.5 rounded-full shrink-0', visual.dot)} />
      <span className="min-w-0 flex-1">
        <span className="flex items-start gap-2">
          <span
            className={cn(
              'flex-1 text-[13px] leading-snug line-clamp-2',
              active
                ? 'text-neutral-900 font-semibold'
                : item.status === 'pending'
                ? 'text-neutral-900 font-medium'
                : 'text-neutral-800'
            )}
          >
            {OBJECT_TYPE_LABELS[item.object_type]}
          </span>
        </span>
        <span className="mt-1 flex items-center gap-1.5 text-[11px] text-neutral-500">
          <span className="px-1.5 h-[17px] inline-flex items-center rounded bg-neutral-100 text-neutral-600 font-mono truncate max-w-[110px]">
            {shortId(item.object_id)}
          </span>
          {requesterName && <span className="truncate">{requesterName}</span>}
          <span className="text-neutral-300">·</span>
          <span className="whitespace-nowrap">{relativeTime(item.created_on)}</span>
        </span>
      </span>
    </button>
  );
};

function ApprovalQueue() {
  const { data: context } = useApprovalContext();
  const isAdmin = !!context?.is_admin;

  const [status, setStatus] = useState<StatusFilter>('pending');
  const [types, setTypes] = useState<string[]>([]);
  const [sources, setSources] = useState<string[]>([]);
  const [range, setRange] = useState<RangeMode>('all');
  const [q, setQ] = useState('');
  const [group, setGroup] = useState<GroupMode>('date');
  const [sort, setSort] = useState<SortMode>('new');
  const [selectedId, setSelectedId] = useApprovalSelection();

  // Souhrnné počty pro taby jen když je uživatel admin.
  const { data: summary } = useApprovalSummary(isAdmin);

  // Server filtruje jen na jediný stav — 'closed'/'all' řešíme klientsky.
  const serverStatus =
    status === 'draft' || status === 'pending' || status === 'approved' || status === 'rejected'
      ? status
      : undefined;
  // Objekt-typ pošleme serveru jen když je vybrán právě jeden.
  const serverObjectType = types.length === 1 ? (types[0] as ObjectType) : undefined;

  const { data, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } = useApprovalsInfinite({
    status: serverStatus,
    objectType: serverObjectType,
  });

  const { data: detail, isLoading: detailLoading } = useApprovalDetail(selectedId);

  // Naakumulované řádky ze všech dosud načtených stránek.
  const loadedRows: ApprovalRequest[] = useMemo(
    () => (data?.pages ?? []).flatMap((p: any) => p?.results ?? []),
    [data]
  );

  const resetPaging = () => {
    setSelectedId(undefined);
  };

  // Infinite scroll. Observer je stabilní (vytvoří se jen při mountu sentinelu),
  // takže střílí pouze při skutečném přechodu do viditelna — ne opakovaně, když
  // sentinel zůstane viditelný u krátkého (vyfiltrovaného) seznamu. To dřív
  // způsobovalo lavinu requestů přes všechny stránky. Aktuální stav čteme přes ref.
  const infiniteState = useRef({ hasNextPage, isFetchingNextPage, fetchNextPage });
  infiniteState.current = { hasNextPage, isFetchingNextPage, fetchNextPage };
  const observerRef = useRef<IntersectionObserver>();
  const sentinelRef = useCallback((node: HTMLDivElement | null) => {
    observerRef.current?.disconnect();
    if (!node) return;
    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (!entries[0].isIntersecting) return;
        const s = infiniteState.current;
        if (s.hasNextPage && !s.isFetchingNextPage) s.fetchNextPage();
      },
      { rootMargin: '120px' }
    );
    observerRef.current.observe(node);
  }, []);

  // Klientská filtrace nad načtenou stránkou.
  const rows = useMemo(() => {
    const search = q.trim().toLowerCase();
    let out = loadedRows.filter((it) => {
      if (status === 'closed' && it.status !== 'approved' && it.status !== 'rejected') return false;
      if (types.length && !types.includes(it.object_type)) return false;
      if (sources.length && !sources.includes(it.request_source)) return false;
      if (!withinRange(it.created_on, range)) return false;
      if (search) {
        const name = it.submitted_by_name ?? '';
        const hay = `${OBJECT_TYPE_LABELS[it.object_type]} ${it.object_id} ${name}`.toLowerCase();
        if (!hay.includes(search)) return false;
      }
      return true;
    });
    out = [...out].sort((a, b) => {
      const ta = new Date(a.created_on).getTime();
      const tb = new Date(b.created_on).getTime();
      return sort === 'old' ? ta - tb : tb - ta;
    });
    return out;
  }, [loadedRows, status, types, sources, range, q, sort]);

  const groups = useMemo(() => {
    if (group === 'none') return [{ key: '__all', rows }];
    const keyOf = (it: ApprovalRequest) =>
      group === 'type'
        ? OBJECT_TYPE_LABELS[it.object_type]
        : group === 'who'
        ? it.submitted_by_name ?? 'Neznámý žadatel'
        : dateBucket(it.created_on);
    const map = new Map<string, ApprovalRequest[]>();
    rows.forEach((it) => {
      const k = keyOf(it);
      if (!map.has(k)) map.set(k, []);
      map.get(k)!.push(it);
    });
    return [...map.entries()].map(([key, r]) => ({ key, rows: r }));
  }, [rows, group]);

  const tabCount = (value: StatusFilter): number | undefined => {
    if (!summary) return undefined;
    if (value === 'pending') return summary.pending_count;
    if (value === 'draft') return summary.draft_count;
    if (value === 'closed') return summary.approved_count + summary.rejected_count;
    if (value === 'all')
      return (
        summary.draft_count +
        summary.pending_count +
        summary.approved_count +
        summary.rejected_count
      );
    return undefined;
  };

  const activeChips = [
    ...types.map((id) => ({
      k: `t-${id}`,
      label: OBJECT_TYPE_LABELS[id as ObjectType],
      rm: () => setTypes(types.filter((x) => x !== id)),
    })),
    ...sources.map((id) => ({
      k: `s-${id}`,
      label: REQUEST_SOURCE_LABELS[id as ApprovalRequestSource],
      rm: () => setSources(sources.filter((x) => x !== id)),
    })),
    ...(range !== 'all'
      ? [
          {
            k: 'r',
            label: RANGE_OPTIONS.find((o) => o.v === range)?.l ?? '',
            rm: () => setRange('all'),
          },
        ]
      : []),
    ...(q ? [{ k: 'q', label: `„${q}"`, rm: () => setQ('') }] : []),
  ];

  const clearAll = () => {
    setTypes([]);
    setSources([]);
    setRange('all');
    setQ('');
    resetPaging();
  };

  // Direct URL for a plain employee: the backend returns an empty queue, explain it.
  if (context && !canSeeApprovalQueue(context)) {
    return (
      <MainContainer>
        <div className="p-6 text-sm">Nejste schvalovatelem.</div>
      </MainContainer>
    );
  }

  return (
    <MainContainer>
      <aside className="w-[352px] shrink-0 border-r border-neutral-150 flex flex-col min-h-0 bg-white">
        {/* hlavička */}
        <div className="px-4 pt-3.5 pb-3 border-b border-neutral-150">
          <div className="flex items-center gap-2">
            <h2 className="text-[15px] font-semibold tracking-tight text-neutral-900">
              Schvalování
            </h2>
            {summary && summary.pending_count > 0 && (
              <Badge variant="warning" size="sm">
                {summary.pending_count} čeká
              </Badge>
            )}
          </div>

          {/* hledání */}
          <div className="relative mt-3">
            <Search
              size={13}
              className="absolute left-2.5 top-1/2 -translate-y-1/2 text-neutral-400 pointer-events-none"
            />
            <input
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                setSelectedId(undefined);
              }}
              placeholder="Hledat v žádostech…"
              data-testid="approval-search-input"
              className="h-8 w-full rounded border border-neutral-200 bg-white pl-8 pr-7 text-[13px] placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500/25 focus:border-primary-500 transition-colors"
            />
            {q && (
              <button
                type="button"
                onClick={() => setQ('')}
                className="absolute right-1.5 top-1/2 -translate-y-1/2 h-5 w-5 inline-flex items-center justify-center rounded text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700"
              >
                <X size={11} />
              </button>
            )}
          </div>

          {/* stavové taby */}
          <div className="mt-2.5 flex items-center gap-1 p-0.5 bg-neutral-100 rounded">
            {STATUS_TABS.map((tab) => {
              const count = tabCount(tab.value);
              const activeTab = status === tab.value;
              return (
                <button
                  key={tab.value}
                  type="button"
                  onClick={() => {
                    setStatus(tab.value);
                    resetPaging();
                  }}
                  data-testid={`approval-status-tab-${tab.value}`}
                  className={cn(
                    'flex-1 min-w-0 h-7 px-1.5 rounded text-[11.5px] font-medium inline-flex items-center justify-center gap-1 transition-colors',
                    activeTab
                      ? 'bg-white text-neutral-900 shadow-sm'
                      : 'text-neutral-600 hover:text-neutral-900'
                  )}
                >
                  <span className="truncate">{tab.label}</span>
                  {count != null && (
                    <span
                      className={cn(
                        'shrink-0 font-mono tabular-nums text-[10px]',
                        activeTab ? 'text-primary-600' : 'text-neutral-400'
                      )}
                    >
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* filtry */}
        <div className="px-4 border-b border-neutral-150 max-h-[46%] overflow-y-auto">
          {activeChips.length > 0 && (
            <div className="pt-2.5 pb-2 flex flex-wrap items-center gap-1.5">
              {activeChips.map((c) => (
                <FilterChip key={c.k} label={c.label} onRemove={c.rm} />
              ))}
              <button
                type="button"
                onClick={clearAll}
                className="h-6 px-1.5 text-[11px] text-neutral-500 hover:text-danger"
              >
                Zrušit vše
              </button>
            </div>
          )}
          <div className="py-1">
            <FacetGroup
              title="Typ žádosti"
              options={OBJECT_TYPE_OPTIONS}
              selected={types}
              onChange={(next) => {
                setTypes(next);
                resetPaging();
              }}
            />
            <FacetGroup
              title="Zdroj"
              options={SOURCE_OPTIONS}
              selected={sources}
              onChange={(next) => {
                setSources(next);
                setSelectedId(undefined);
              }}
              defaultOpen={false}
            />
            <div className="border-t border-neutral-100 py-2.5">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-neutral-500 mb-1.5">
                Období
              </div>
              <div className="flex flex-wrap gap-1">
                {RANGE_OPTIONS.map((o) => (
                  <button
                    key={o.v}
                    type="button"
                    onClick={() => {
                      setRange(o.v);
                      setSelectedId(undefined);
                    }}
                    className={cn(
                      'h-6 px-2 rounded border text-[11px] font-medium transition-colors',
                      range === o.v
                        ? 'bg-primary-600 border-primary-600 text-white'
                        : 'bg-white border-neutral-200 text-neutral-600 hover:border-neutral-300 hover:text-neutral-900'
                    )}
                  >
                    {o.l}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* lišta seznamu */}
        <div className="px-3 h-9 flex items-center gap-2 border-b border-neutral-150 bg-neutral-25">
          <span className="text-[11px] text-neutral-500">
            <span className="font-semibold text-neutral-800 tabular-nums">{rows.length}</span>{' '}
            žádostí
          </span>
          <div className="ml-auto flex items-center gap-1">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="h-6 px-1.5 inline-flex items-center gap-1 rounded text-[11px] text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900"
                >
                  <Layers size={11} />
                  {GROUP_LABELS[group]}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Seskupit</DropdownMenuLabel>
                {(Object.keys(GROUP_LABELS) as GroupMode[]).map((v) => (
                  <DropdownMenuItem key={v} onClick={() => setGroup(v)}>
                    {group === v ? <Check size={12} /> : <span className="w-3 inline-block" />}
                    {GROUP_LABELS[v]}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="h-6 px-1.5 inline-flex items-center gap-1 rounded text-[11px] text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900"
                >
                  <ChevronsUpDown size={11} />
                  {SORT_LABELS[sort]}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Řadit</DropdownMenuLabel>
                {(Object.keys(SORT_LABELS) as SortMode[]).map((v) => (
                  <DropdownMenuItem key={v} onClick={() => setSort(v)}>
                    {sort === v ? <Check size={12} /> : <span className="w-3 inline-block" />}
                    {SORT_LABELS[v]}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* seznam */}
        <div className="flex-1 overflow-y-auto min-h-0">
          <SpinnerOverlay spinning={isLoading}>
            {rows.length === 0 ? (
              <div className="px-6 py-12 text-center">
                <div className="mx-auto h-9 w-9 rounded-full bg-neutral-100 flex items-center justify-center mb-3">
                  <Search size={15} className="text-neutral-400" />
                </div>
                <div className="text-[13px] font-medium text-neutral-800">
                  Nic neodpovídá filtru
                </div>
                <div className="text-[12px] text-neutral-500 mt-1">
                  Zkuste zrušit část filtrů nebo hledaný výraz.
                </div>
                {activeChips.length > 0 && (
                  <Button variant="outline" size="sm" className="mt-3" onClick={clearAll}>
                    Zrušit všechny filtry
                  </Button>
                )}
              </div>
            ) : (
              groups.map((g) => (
                <div key={g.key}>
                  {g.key !== '__all' && (
                    <div className="sticky top-0 z-10 px-3 h-7 flex items-center gap-2 bg-neutral-25/95 backdrop-blur-sm border-b border-neutral-100">
                      <span className="text-[10.5px] font-semibold uppercase tracking-wider text-neutral-500">
                        {g.key}
                      </span>
                      <span className="text-[10.5px] font-mono text-neutral-400">
                        {g.rows.length}
                      </span>
                    </div>
                  )}
                  <div className="divide-y divide-neutral-100">
                    {g.rows.map((it) => (
                      <RequestRow
                        key={it.id}
                        item={it}
                        active={selectedId === it.id}
                        requesterName={it.submitted_by_name}
                        onClick={() => setSelectedId(it.id)}
                      />
                    ))}
                  </div>
                </div>
              ))
            )}
            {hasNextPage && (
              <div
                ref={sentinelRef}
                data-testid="approval-list-sentinel"
                className="h-10 flex items-center justify-center text-[11px] text-neutral-400"
              >
                {isFetchingNextPage ? 'Načítám…' : ''}
              </div>
            )}
          </SpinnerOverlay>
        </div>
      </aside>

      <ApprovalDetail request={detail} loading={!!selectedId && detailLoading} />
    </MainContainer>
  );
}

export default ApprovalQueue;
